<template>
  <div class="acct-wrap">
    <HeaderComponent />

    <div class="acct-container">
      <div class="acct-header">
        <div>
          <h1>{{ $t('dashboard.welcome') }}<span v-if="displayName">, {{ displayName }}</span></h1>
          <p class="acct-phone">{{ phone }}</p>
        </div>
        <button class="btn-signout" @click="signOut">{{ $t('nav.signOut') }}</button>
      </div>

      <div class="acct-metrics">
        <div class="metric">
          <div class="metric-value">{{ summary.unpaidCount }}</div>
          <div class="metric-label">{{ $t('dashboard.arrears') }}</div>
        </div>
        <div class="metric">
          <div class="metric-value">KES {{ formatMoney(summary.unpaidTotal) }}</div>
          <div class="metric-label">{{ $t('form.amount') }}</div>
        </div>
      </div>

      <section class="acct-section">
        <h2>{{ $t('nav.services') }} — {{ $t('receipt.billTotal') }}</h2>
        <div v-if="loading" class="empty">{{ $t('messages.loading') }}</div>
        <div v-else-if="!summary.recentBills.length" class="empty">{{ $t('messages.noResults') }}</div>
        <table v-else class="acct-table">
          <thead>
            <tr>
              <th>{{ $t('form.billNumber') }}</th>
              <th>{{ $t('nav.services') }}</th>
              <th class="ta-r">{{ $t('receipt.billTotal') }}</th>
              <th class="ta-r">{{ $t('receipt.balance') }}</th>
              <th>{{ $t('form.required') === 'Required' ? 'Status' : 'Hali' }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in summary.recentBills" :key="b.billNo">
              <td class="mono">{{ b.billNo }}</td>
              <td>
                <span class="badge">{{ b.incomeTypePrefix }}</span>
                {{ truncate(b.feeDescription, 40) }}
              </td>
              <td class="ta-r">{{ formatMoney(b.detailAmount) }}</td>
              <td class="ta-r">{{ formatMoney(b.billBalance) }}</td>
              <td>
                <span class="status" :class="statusClass(b.status)">{{ b.status }}</span>
              </td>
              <td>
                <a
                  v-if="b.status !== 'PAID' && !b.billNo.includes('•')"
                  :href="`/#/pay?bill=${b.billNo}`"
                  class="btn-pay"
                >{{ $t('actions.payNow') }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="acct-section">
        <h2>{{ $t('dashboard.receipts') }}</h2>
        <div v-if="loading" class="empty">{{ $t('messages.loading') }}</div>
        <div v-else-if="!summary.recentReceipts.length" class="empty">{{ $t('dashboard.noReceipts') }}</div>
        <table v-else class="acct-table">
          <thead>
            <tr>
              <th>{{ $t('receipt.receiptNumber') }}</th>
              <th>{{ $t('form.billNumber') }}</th>
              <th class="ta-r">{{ $t('receipt.receiptAmount') }}</th>
              <th>{{ $t('nav.services') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in summary.recentReceipts" :key="r.receiptNo">
              <td class="mono">{{ r.receiptNo }}</td>
              <td class="mono">{{ r.billNo }}</td>
              <td class="ta-r">{{ formatMoney(r.receiptAmount) }}</td>
              <td>{{ truncate(r.feeDescription, 40) }}</td>
              <td>
                <a :href="`/#/print-receipt?receipt=${r.receiptNo}`" class="btn-mini">
                  {{ $t('actions.print') }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { biller, authUrl, execute } from "@/api";
import * as session from "@/utils/citizenSession";

export default {
  name: "CitizenMyAccount",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      loading: true,
      phone: session.getPhone() || '',
      displayName: session.getName() || '',
      summary: { unpaidCount: 0, unpaidTotal: 0, recentBills: [], recentReceipts: [] },
    };
  },
  mounted() {
    if (!session.isAuthed()) {
      this.$router.replace('/sign-in');
      return;
    }
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append('function', 'citizenSummary');
        fd.append('token', session.getToken());
        const res = await execute(fd, biller);
        if (res.data.success) {
          this.summary = res.data.data;
          this.phone = res.data.data.phone;
        } else if ((res.data.message || '').toLowerCase().includes('session')) {
          session.clear();
          this.$router.replace('/sign-in');
        }
      } catch (e) { /* silent */ }
      finally { this.loading = false; }
    },
    async signOut() {
      const token = session.getToken();
      if (token) {
        try {
          const fd = new FormData();
          fd.append('function', 'citizenSignOut');
          fd.append('token', token);
          await execute(fd, authUrl);
        } catch (_) {}
      }
      session.clear();
      this.$router.replace('/');
    },
    formatMoney(v) {
      const n = Number(v || 0);
      if (isNaN(n)) return v || '0';
      return new Intl.NumberFormat('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
    },
    truncate(s, n) {
      s = String(s || '');
      return s.length > n ? s.slice(0, n) + '…' : s;
    },
    statusClass(s) {
      return {
        'st-paid':    s === 'PAID',
        'st-partial': s === 'PARTIAL',
        'st-unpaid':  s === 'UNPAID',
      };
    },
  },
};
</script>

<style scoped>
.acct-wrap { min-height: 100vh; display: flex; flex-direction: column; background: #f6f8fa; }
.acct-container { max-width: 1080px; width: 100%; margin: 24px auto 48px; padding: 0 20px; }
.acct-header {
  display: flex; align-items: flex-end; justify-content: space-between;
  padding: 4px 0 20px;
}
.acct-header h1 { margin: 0; font-size: 22px; color: #1f2328; }
.acct-phone { margin: 4px 0 0; color: #6e7781; font-size: 13px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; }
.btn-signout {
  background: #fff; border: 1px solid #d0d7de; border-radius: 8px;
  padding: 8px 14px; font-size: 13px; cursor: pointer; color: #24292f;
}
.acct-metrics { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 4px 0 24px; }
.metric { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 12px rgba(0,0,0,.04); }
.metric-value { font-size: 26px; font-weight: 700; color: #1f2328; }
.metric-label { font-size: 12px; color: #6e7781; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 6px; }
.acct-section { background: #fff; border-radius: 12px; padding: 20px; margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,.04); }
.acct-section h2 { margin: 0 0 14px; font-size: 15px; color: #1f2328; text-transform: uppercase; letter-spacing: 0.5px; }
.empty { color: #6e7781; font-size: 14px; padding: 16px 0; text-align: center; }
.acct-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.acct-table th, .acct-table td { text-align: left; padding: 10px 12px; border-bottom: 1px solid #eaeef2; }
.acct-table th { font-size: 11px; color: #6e7781; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.ta-r { text-align: right; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; font-size: 12px; }
.badge {
  display: inline-block; background: #eaeef2; color: #24292f;
  padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; margin-right: 6px;
}
.status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
.st-paid    { background: #dafbe1; color: #116329; }
.st-partial { background: #fff8c5; color: #7a5901; }
.st-unpaid  { background: #ffebe9; color: #a40e26; }
.btn-pay {
  background: #1f6feb; color: #fff; padding: 6px 12px; border-radius: 6px;
  text-decoration: none; font-size: 12px; font-weight: 600;
}
.btn-mini {
  background: #f6f8fa; color: #24292f; padding: 6px 12px; border-radius: 6px;
  text-decoration: none; font-size: 12px; border: 1px solid #d0d7de;
}
@media (max-width: 640px) {
  .acct-metrics { grid-template-columns: 1fr; }
  .acct-table { font-size: 12px; }
  .acct-table th, .acct-table td { padding: 8px 6px; }
  .acct-header { flex-direction: column; align-items: flex-start; gap: 10px; }
}
</style>
